import { PercentageOutlined } from "@ant-design/icons";
import {
  useCreateBrandBilling,
  useEditBrandBilling,
} from "@entities/billingBrand/lib/hooks";
import {
  useCreateInitialBilling,
  useEditSingleModeBilling,
} from "@entities/organisation/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { IBrandBilling } from "@shared/lib/types/brandBilling";
import { Button, Col, Form, Input, InputNumber, Modal, Radio, Row } from "antd";
import { CreditCard, Edit2, Hash, IndianRupee, Mail } from "lucide-react";
import React, { useEffect } from "react";
import styles from "./styles.module.scss";

interface BillingModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: IBrandBilling;
  mode: "create" | "edit" | "createBrandBilling" | "editBrandBilling";
  organisationId: string;
  brandId?: string;
}

const BillingModal: React.FC<BillingModalProps> = ({
  open,
  onClose,
  initialData,
  mode,
  organisationId,
  brandId,
}) => {
  const [form] = Form.useForm();
  const { edit, isEditing, isEdited, editSingeModeError } =
    useEditSingleModeBilling(organisationId);
  const {
    create: createInitialBilling,
    isCreating,
    isCreated,
    createInitialBillingError,
  } = useCreateInitialBilling(organisationId);

  const {
    create: createBrandBilling,
    isCreating: isBrandBillingCreating,
    isCreated: isBrandBillingCreated,
    error: createBrandBillingError,
  } = useCreateBrandBilling(organisationId);

  const {
    edit: editBrandBilling,
    isEditing: isBrandBillingEditing,
    isEdited: isBrandBillingEdited,
    error: editBrandBillingError,
  } = useEditBrandBilling(organisationId, brandId);

  useEffect(() => {
    if (open && initialData) {
      form.setFieldsValue(initialData);
    }
    if (open && !initialData) {
      form.resetFields();
    }
  }, [open, initialData, form]);

  useEffect(() => {
    if (isEdited) {
      ToastService.success("Billing details updated successfully");
      onClose();
    } else if (editSingeModeError) {
      ToastService.error(editSingeModeError.message);
    }
  }, [isEdited, editSingeModeError]);

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Billing details created successfully");
      onClose();
    } else if (createInitialBillingError) {
      ToastService.error(createInitialBillingError.message);
    }
  }, [isCreated, createInitialBillingError]);

  useEffect(() => {
    if (isBrandBillingCreated) {
      ToastService.success("Brand Billing details created successfully");
      onClose();
    } else if (createBrandBillingError) {
      ToastService.error(createBrandBillingError.message);
    }
  }, [isBrandBillingCreated, createBrandBillingError]);

  useEffect(() => {
    if (isBrandBillingEdited) {
      ToastService.success("Brand Billing details updated successfully");
      onClose();
    } else if (editBrandBillingError) {
      ToastService.error(editBrandBillingError.message);
    }
  }, [isBrandBillingEdited, editBrandBillingError]);

  const title =
    mode === "create" || mode === "createBrandBilling"
      ? "Add Billing Details"
      : "Edit Billing Details";

  const onFinish = (values: any) => {
    if (mode === "edit") {
      edit({
        brandOrganisationId: organisationId,
        brandBillingDetails: values,
      });
    } else if (mode === "createBrandBilling") {
      createBrandBilling({
        brandBilling: {
          brandId: brandId,
          ...values,
        },
      });
    } else if (mode === "editBrandBilling") {
      editBrandBilling({
        billingId: initialData?.billingId || "",
        brandBilling: {
          // brandId: brandId,
          ...values,
        },
      });
    } else {
      const vals = { ...values };
      delete vals.billingMode;
      createInitialBilling({
        brandOrganisationId: organisationId,
        billingMode: values.billingMode,
        brandBillingDetails: values.billingMode === "Single" ? [vals] : [],
      });
    }
  };

  const formMode = Form.useWatch("billingMode", form);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={
        <div className={styles.footer}>
          <Button
            onClick={onClose}
            disabled={
              isEditing ||
              isCreating ||
              isBrandBillingCreating ||
              isBrandBillingEditing
            }
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form="billing-form"
            loading={
              isEditing ||
              isCreating ||
              isBrandBillingCreating ||
              isBrandBillingEditing
            }
          >
            {mode === "create" ? "Save" : "Save Changes"}
          </Button>
        </div>
      }
      title={
        <div className={styles.modalTitle}>
          <div className={styles.titleIcon}>
            {mode === "create" ? <CreditCard size={20} /> : <Edit2 size={20} />}
          </div>
          {title}
        </div>
      }
      className={styles.billingModal}
      styles={{
        header: {
          borderBottom: "1px solid #e3e8ee",
          padding: 20,
        },
        content: {
          padding: 0,
        },
        body: {
          padding: "20px 20px 0 20px",
        },
        footer: {
          borderTop: "1px solid #e3e8ee",
          padding: "0 20px 20px 20px",
        },
      }}
    >
      <Form
        form={form}
        requiredMark={false}
        name="billing-form"
        layout="vertical"
        onFinish={onFinish}
      >
        {mode === "create" ? (
          <Form.Item
            name={"billingMode"}
            label="Billing Mode"
            rules={[
              {
                required: true,
                message: "Please select the billing mode",
              },
            ]}
          >
            <Radio.Group
              options={[
                { label: "Single", value: "Single" },
                { label: "Multiple", value: "Multiple" },
              ]}
            />
          </Form.Item>
        ) : null}
        {mode !== "create" || (mode === "create" && formMode === "Single") ? (
          <>
            <Form.Item
              name="name"
              label="Party Name"
              rules={[
                { required: true, message: "Please enter the party name" },
              ]}
            >
              <Input placeholder="Enter party name" />
            </Form.Item>
            <Form.Item
              name="emailId"
              label="Billing Email"
              // rules={[
              //   { required: true, message: "Please enter the billing email" },
              //   { type: "email", message: "Please enter a valid email" },
              // ]}
            >
              <Input
                prefix={<Mail size={16} className={styles.inputIcon} />}
                placeholder="Enter billing email"
              />
            </Form.Item>
            <div className={styles.row}>
              <Form.Item
                name="gst"
                label="GSTIN"
                rules={[{ required: true, message: "Please enter the GSTIN" }]}
                className={styles.halfWidth}
              >
                <Input
                  prefix={<Hash size={16} className={styles.inputIcon} />}
                  placeholder="Enter GSTIN"
                />
              </Form.Item>
              <Form.Item
                name="pan"
                label="PAN"
                // rules={[{ required: true, message: "Please enter the PAN" }]}
                className={styles.halfWidth}
              >
                <Input
                  prefix={<Hash size={16} className={styles.inputIcon} />}
                  placeholder="Enter PAN"
                />
              </Form.Item>
            </div>
            {/* <Form.Item
          name={["address", "addressLine1"]}
          label="Address"
          rules={[{ required: true, message: "Please enter the address" }]}
        >
          <Input.TextArea rows={3} placeholder="Enter address" />
        </Form.Item> */}
            <Form.Item
              name={["address", "addressLine1"]}
              label="Street Address 1"
              rules={[
                { required: true, message: "Street address is required" },
              ]}
            >
              <Input placeholder="Flat No., Society, Street Number etc" />
            </Form.Item>
            <Form.Item
              name={["address", "addressLine2"]}
              label="Street Address 2"
              rules={[
                { required: true, message: "Street address is required" },
              ]}
            >
              <Input placeholder="Flat No., Society, Street Number etc" />
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name={["address", "city"]}
                  label="City"
                  rules={[{ required: true, message: "City is required" }]}
                >
                  <Input placeholder="City" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["address", "state"]}
                  label="State"
                  rules={[{ required: true, message: "State is required" }]}
                >
                  <Input placeholder="State" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name={["address", "country"]}
                  label="Country"
                  rules={[{ required: true, message: "Country is required" }]}
                >
                  <Input placeholder="Country" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["address", "pinCode"]}
                  label="Pin Code"
                  rules={[{ required: true, message: "Pin code is required" }]}
                >
                  <Input placeholder="Pin Code" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="gstRate"
                  label="GST Rate"
                  rules={[{ required: true, message: "GST rate is required" }]}
                >
                  <InputNumber
                    placeholder="GST Rate"
                    type="number"
                    style={{ width: "100%" }}
                    suffix={<PercentageOutlined size={16} />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={"serviceChargeRate"}
                  label="Service Charge Rate"
                  rules={[
                    {
                      required: true,
                      message: "Service charge rate is required",
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Service Charge Rate"
                    style={{ width: "100%" }}
                    suffix={<PercentageOutlined size={16} />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="paymentTermsInDays"
                  label="Payment Terms"
                  rules={[
                    { required: true, message: "Payment terms is required" },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Payment Terms in Days"
                    style={{ width: "100%" }}
                    suffix={"Days"}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={"creditLimit"}
                  label="Credit Limit"
                  rules={[
                    {
                      required: true,
                      message: "Credit limit is required",
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Credit Limit"
                    style={{ width: "100%" }}
                    prefix={<IndianRupee size={16} />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={"sacCode"}
                  label="SAC Code"
                  rules={[{ required: true, message: "SAC code is required" }]}
                >
                  <Input placeholder="SAC Code" />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : null}
      </Form>
    </Modal>
  );
};

export default BillingModal;
